import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IMG } from "@/lib/site";
import { Reveal } from "@/components/Reveal";
import { SectionHeader } from "@/components/SectionHeader";

const CATEGORIES = [
  {
    title: "Plywood & Panel Products",
    body: "Premium plywood, boards and engineered panel solutions for residential and commercial interiors.",
  },
  {
    title: "Decorative Laminates",
    body: "Contemporary and designer laminates for kitchens, wardrobes, furniture and architectural applications.",
  },
  {
    title: "Decorative Louvers & Wall Panels",
    body: "Modern wall solutions that enhance aesthetics and create sophisticated living environments.",
  },
  {
    title: "Architectural Hardware",
    body: "Premium hardware solutions for residential and commercial projects.",
  },
  {
    title: "Kitchen Fittings & Accessories",
    body: "Functional and elegant fittings designed for modern kitchens.",
  },
  {
    title: "Wardrobe Fittings & Accessories",
    body: "Premium storage solutions and accessories for organized and luxurious wardrobes.",
  },
  {
    title: "Bathroom Accessories",
    body: "Curated bathroom accessories combining aesthetics and functionality.",
  },
  {
    title: "Cabinet Handles Collection",
    body: "650+ handle designs on display — one of the region's largest collections.",
    badge: "650+ Designs",
  },
  {
    title: "Main Door Handles Collection",
    body: "300+ premium main door handle designs on display.",
    badge: "300+ Designs",
  },
  {
    title: "Mortise Handles Collection",
    body: "300+ mortise handle designs on display.",
    badge: "300+ Designs",
  },
  {
    title: "Mechanical Locks",
    body: "Reliable and premium locking solutions for homes and commercial applications.",
  },
  {
    title: "Digital Locks & Smart Access",
    body: "Modern digital locking systems combining convenience, security and elegant design.",
  },
];

const SHOWCASE = [
  { title: "650+", label: "Cabinet Handle Designs" },
  { title: "300+", label: "Main Door Handle Designs" },
  { title: "300+", label: "Mortise Handle Designs" },
  { title: "20+", label: "Premium Interior Brands" },
  { title: "1:1", label: "Expert Product Guidance" },
  { title: "All-in-One", label: "Smart Living & Interiors" },
];

export default function Interior() {
  return (
    <>
      <section className="relative pt-40 pb-20" data-testid="interior-hero">
        <div className="container-edge max-w-4xl">
          <p className="eyebrow flex items-center gap-3"><span className="divider-gold" /> Premium Interior Solutions</p>
          <h1 className="display-1 mt-6">
            Curated materials & hardware for <span className="italic text-gold">exceptional</span> spaces.
          </h1>
          <p className="mt-8 text-base md:text-lg text-muted-foreground leading-relaxed">
            From plywood and laminates to over 650 cabinet handle designs — one destination for the finest
            premium interior materials and hardware in Coimbatore.
          </p>
        </div>
      </section>

      {/* Showcase highlights */}
      <section className="py-16 border-y border-border bg-secondary/40" data-testid="showcase-highlights">
        <div className="container-edge">
          <Reveal>
            <p className="eyebrow text-center flex items-center justify-center gap-3"><span className="divider-gold" /> Experience One of Coimbatore's Largest Display Collections <span className="divider-gold" /></p>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-border">
            {SHOWCASE.map((s, i) => (
              <Reveal key={i} delay={i * 60} className="bg-background p-6 text-center">
                <p className="font-display text-3xl md:text-4xl text-gold">{s.title}</p>
                <p className="mt-3 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Categories grid - typography driven */}
      <section className="py-24 md:py-32" data-testid="interior-categories">
        <div className="container-edge">
          <Reveal>
            <SectionHeader eyebrow="Categories" title={<>Twelve curated <span className="italic text-gold">collections</span>.</>} />
          </Reveal>
          <div className="mt-20 grid md:grid-cols-2 gap-px bg-border border border-border">
            {CATEGORIES.map((c, i) => (
              <Reveal
                key={c.title}
                delay={(i % 2) * 100}
                className="group relative bg-background hover:bg-charcoal-900 transition-colors duration-500 p-8 md:p-10"
              >
                <article className="relative" data-testid={`interior-cat-${i}`}>
                  {/* Number */}
                  <div className="flex items-start justify-between gap-4 mb-6">
                    <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">
                      {String(i + 1).padStart(2, "0")} · Collection
                    </span>
                    {c.badge && (
                      <span
                        className="font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 bg-gold text-charcoal-900 group-hover:bg-white transition-colors"
                        data-testid={`interior-cat-${i}-badge`}
                      >
                        {c.badge}
                      </span>
                    )}
                  </div>

                  {/* Gold line — grows on hover */}
                  <span className="block h-px w-10 bg-gold mb-8 transition-all duration-700 group-hover:w-24" />

                  {/* Title */}
                  <h3 className="font-display text-2xl md:text-3xl lg:text-[32px] font-light leading-[1.15] tracking-[-0.03em] text-foreground group-hover:text-white transition-colors">
                    {c.title}
                  </h3>

                  {/* Body */}
                  <p className="mt-5 text-sm md:text-[15px] leading-relaxed text-muted-foreground group-hover:text-white/70 transition-colors max-w-md">
                    {c.body}
                  </p>

                  {/* Corner accent */}
                  <div className="absolute -top-8 -right-8 md:-top-10 md:-right-10 font-display text-[120px] md:text-[160px] leading-none font-light text-gold/[0.04] group-hover:text-gold/20 transition-colors pointer-events-none select-none">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <div className="mt-16 text-center">
            <Link to="/contact" data-testid="interior-visit" className="btn-gold">
              Visit Our Experience Centre <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
